import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../context/AuthContext';
import { useUserStatus } from '../context/UserStatusContext';
import { useProfileCompletion } from '../context/ProfileCompletionContext';
import { useGetAvailability } from './useGetAvailability';
import { useGetReservations } from './useGetReservations';
import { IPatient } from '../types/Patient/Patient.type';

export type StepStatus = 'pending' | 'current' | 'completed';

export interface DashboardStep {
    id: string;
    label: string;
    status: StepStatus;
    actionLabel?: string;
    actionPath?: string;
    description?: string;
}

export const useDashboardSteps = () => {
    const { t } = useTranslation();
    const { user } = useAuthContext();
    const { userStatusData, loading: statusLoading } = useUserStatus();
    const { profileData, isProfileComplete, isPersonalInfoComplete, loading: completionLoading } = useProfileCompletion();
    const { data: availabilityData, loading: availabilityLoading } = useGetAvailability();
    const { data: reservationsData, loading: reservationsLoading } = useGetReservations();

    const loading = statusLoading || completionLoading || availabilityLoading || reservationsLoading;

    const isPatientProfileComplete = useMemo(() => {
        if (!user || user.type_account === 'professional') return false;

        // Cast user to IPatient to access details
        const patient = user as unknown as IPatient;

        const hasBasicInfo = !!(patient.first_name && patient.last_name);
        const details = patient.details || {};
        const hasDetails = !!(
            details.date_birth &&
            details.gender &&
            details.phone &&
            (details.country || (details as any).country_code) &&
            details.city &&
            details.address
        );

        return hasBasicInfo && hasDetails;
    }, [user]);

    const hasBookings = useMemo(() => {
        return reservationsData && reservationsData.length > 0;
    }, [reservationsData]);

    const steps = useMemo<DashboardStep[]>(() => {
        if (!user) return [];

        const isProfessional = user.type_account === 'professional';
        const stepsList: DashboardStep[] = [];

        if (isProfessional) {
            // Step 1: Personal Info
            const personalInfoStatus: StepStatus = isPersonalInfoComplete ? 'completed' : 'current';
            stepsList.push({
                id: 'personal_info',
                label: t('dashboard.steps.professional.personalInfo', 'Datos personales'),
                status: personalInfoStatus,
                actionLabel: t('dashboard.steps.action.finish', 'Completar'),
                actionPath: '/personal-info'
            });

            // Step 2: Professional Profile
            const profileStatus: StepStatus = isProfileComplete ? 'completed' : (personalInfoStatus === 'completed' ? 'current' : 'pending');
            stepsList.push({
                id: 'professional_profile',
                label: t('dashboard.steps.professional.professionalProfile', 'Perfil profesional'),
                status: profileStatus,
                actionLabel: t('dashboard.steps.action.finish', 'Completar'),
                actionPath: '/profile'
            });

            // Step 3: Add Availability
            let availabilityStatus: StepStatus = 'pending';
            const isFullProfileComplete = isProfileComplete && isPersonalInfoComplete;
            
            if (isFullProfileComplete) {
                const bookingMode = (profileData?.details as any)?.booking_mode;
                if ((availabilityData && availabilityData.length > 0) || bookingMode === 'request') {
                    availabilityStatus = 'completed';
                } else {
                    availabilityStatus = 'current';
                }
            }

            stepsList.push({
                id: 'add_availability',
                label: t('dashboard.steps.professional.addAvailability', 'Comparte tu disponibilidad'),
                status: availabilityStatus,
                actionLabel: t('dashboard.steps.action.configure', 'Configurar'),
                actionPath: '/availability'
            });
        } else {
            // Patient Flow
            // Step 1: Complete Profile
            // Use strict validation for Patient
            const profileStepStatus: StepStatus = isPatientProfileComplete ? 'completed' : 'current';
            stepsList.push({
                id: 'complete_profile',
                label: t('dashboard.steps.patient.completeProfile'),
                status: profileStepStatus,
                actionLabel: profileStepStatus !== 'completed' ? t('dashboard.steps.action.complete') : undefined,
                actionPath: '/personal-info'
            });

            // Step 2: Search Professionals (Find your Guide)
            // Completed if has at least one booking (attempt or confirmed)
            let searchStatus: StepStatus = 'pending';
            if (profileStepStatus === 'completed') {
                searchStatus = hasBookings ? 'completed' : 'current';
            }

            stepsList.push({
                id: 'search_professionals',
                label: t('dashboard.steps.patient.searchProfessionals'),
                status: searchStatus,
                actionLabel: searchStatus === 'current' ? t('dashboard.steps.action.search') : undefined,
                actionPath: '/#search-section'
            });
        }

        return stepsList;
    }, [user, t, isProfileComplete, isPersonalInfoComplete, userStatusData, availabilityData, isPatientProfileComplete, hasBookings, profileData]);

    return { steps, loading };
};
